/**
 * @file Core domain types for the uploads library.
 * Defines session/file models, enqueue params, and the provider context contract.
 */

import type {
  UploadEnqueueEntryPoint,
  UploadEnqueueSource,
} from '@/lib/analytics/events';

/** Registered upload kind identifier (e.g. `transition-document`). */
export type UploadKind = string;

export type { UploadEnqueueEntryPoint, UploadEnqueueSource };

/** Lifecycle status of a single file within an upload session. */
export type UploadFileStatus =
  | 'queued'
  | 'uploading'
  | 'paused'
  | 'complete'
  | 'error'
  | 'cancelled';

/** React/UI representation of one file tracked by Uppy within a session. */
export type UploadFileState = {
  /** Uppy-assigned file id. */
  uppyFileId: string;
  fileName: string;
  bytesTotal: number;
  /** Progress percentage 0–100. */
  progress: number;
  status: UploadFileStatus;
  errorMessage?: string;
};

/**
 * A logical upload batch scoped to one resource (e.g. one transition).
 * Session `id` equals `resourceKey` (`scope:id` format).
 */
export type UploadResourceSession = {
  id: string;
  kind: UploadKind;
  /** Display label shown in the activity panel header. */
  label: string;
  /** SPA path to navigate when user clicks session header or restarts upload. */
  returnPath: string;
  /** Kind-specific metadata persisted across reload (e.g. `transitionId`). */
  meta: Record<string, unknown>;
  files: UploadFileState[];
  /** When true, session is hidden from the activity panel after user dismisses. */
  dismissed: boolean;
  /** ISO timestamp set when all files reach a settled state. */
  completedAt?: string;
};

/** Arguments passed to {@link UploadManagerContextValue.enqueue}. */
export type EnqueueUploadParams = {
  kind: UploadKind;
  /** Unique session key, typically from {@link buildResourceKey}. */
  resourceKey: string;
  label: string;
  returnPath: string;
  meta: Record<string, unknown>;
  files: File[];
  /** How the user selected the files (drag vs file picker). */
  source: UploadEnqueueSource;
  /** Which UI surface initiated the enqueue. */
  entryPoint: UploadEnqueueEntryPoint;
};

/**
 * IndexedDB-persisted S3 multipart registration state for resume after reload.
 * @internal Used by persistence and Uppy S3 plugin.
 */
export type PersistedMultipartContext = {
  uppyFileId: string;
  uploadId: string;
  resourceKey: string;
  partUrlStore: Record<number, string>;
  registeredAt: string;
};

/** Statuses considered "active" for stall detection and UI affordances. */
export const UPLOAD_ACTIVE_STATUSES: UploadFileStatus[] = [
  'queued',
  'uploading',
  'paused',
  'error',
];

/** Statuses where upload is in flight (excludes terminal error for count purposes). */
export const UPLOAD_IN_PROGRESS_STATUSES: UploadFileStatus[] = [
  'queued',
  'uploading',
  'paused',
];

/** Presigned URL validity window used to detect expired multipart contexts. */
export const PRESIGNED_URL_TTL_MS = 60 * 60 * 1000;

/** Soft cap while sessions are still within the retention window. */
export const MAX_UNDISMISSED_COMPLETED_SESSIONS = 50;

/** Value exposed by {@link UploadManagerContext} via {@link useUploadManager}. */
export type UploadManagerContextValue = {
  sessions: UploadResourceSession[];
  activeUploadCount: number;
  /** Increments when the uploads panel should open; use with openUploadsPanel. */
  activityPanelOpenNonce: number;
  openUploadsPanel: () => void;
  enqueue: (params: EnqueueUploadParams) => void;
  cancelFile: (uppyFileId: string) => void;
  cancelSession: (resourceKey: string) => void;
  pauseResumeFile: (uppyFileId: string) => void;
  dismissSession: (resourceKey: string) => void;
  /** False until hydration/recovery completes on mount. */
  isReady: boolean;
};
